package com.qrkontaq.ui.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.core.auth.Session
import com.qrkontaq.data.dto.MyRestaurantDto
import com.qrkontaq.data.repository.AdminRepository
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.theme.Brand
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh

enum class AdminTab(val iconSvg: String) {
    DASHBOARD(AdminIcons.Dashboard),
    MENU(com.qrkontaq.ui.tenant.MenuIcons.Utensils),
    STOP_LIST(AdminIcons.Ban),
    TABLES(AdminIcons.QrCode),
    ORDERS(AdminIcons.ChefHat),
    STAFF(AdminIcons.Users),
    SETTINGS(AdminIcons.Sliders),
    SUBSCRIPTION(AdminIcons.CreditCard),
    ;

    val title: String
        get() = when (this) {
            DASHBOARD -> AdminT.tabDashboard
            MENU -> AdminT.tabMenu
            STOP_LIST -> AdminT.tabStopList
            TABLES -> AdminT.tabTables
            ORDERS -> AdminT.tabOrders
            STAFF -> AdminT.tabStaff
            SETTINGS -> AdminT.tabSettings
            SUBSCRIPTION -> AdminT.tabSubscription
        }

    val subtitle: String
        get() = when (this) {
            DASHBOARD -> AdminT.tabDashboardSub
            MENU -> AdminT.tabMenuSub
            STOP_LIST -> AdminT.tabStopListSub
            TABLES -> AdminT.tabTablesSub
            ORDERS -> AdminT.tabOrdersSub
            STAFF -> AdminT.tabStaffSub
            SETTINGS -> AdminT.tabSettingsSub
            SUBSCRIPTION -> AdminT.tabSubscriptionSub
        }
}

/** First finger (touch) or pointer coordinates of an event. */
private fun touchPointXY(e: org.w3c.dom.events.Event): Pair<Double, Double> {
    val d = e.asDynamic()
    return if (d.touches != null && d.touches.length as Int > 0) {
        (d.touches[0].clientX.unsafeCast<Double>()) to (d.touches[0].clientY.unsafeCast<Double>())
    } else {
        (d.clientX.unsafeCast<Double>()) to (d.clientY.unsafeCast<Double>())
    }
}

private fun tabsFor(role: String): List<AdminTab> =
    when (role) {
        // Waiter (and legacy STAFF): a personal overview, the floor and the orders.
        "WAITER", "STAFF" -> listOf(AdminTab.DASHBOARD, AdminTab.TABLES, AdminTab.ORDERS)
        // Cook / bartender: a personal overview, then orders; the floor is view-only.
        "COOK", "BARTENDER" -> listOf(AdminTab.DASHBOARD, AdminTab.ORDERS, AdminTab.TABLES)
        // Manager: everything except the subscription (and the stop-list lives in Menu).
        "MANAGER" ->
            listOf(
                AdminTab.DASHBOARD, AdminTab.MENU, AdminTab.TABLES,
                AdminTab.ORDERS, AdminTab.STAFF, AdminTab.SETTINGS,
            )
        else -> AdminTab.entries.filter { it != AdminTab.STOP_LIST }
    }

@Composable
fun AdminShell(
    repository: AdminRepository,
    scope: CoroutineScope,
    restaurants: List<MyRestaurantDto>,
    activeId: String,
    onLogout: () -> Unit,
    onRestaurantDeleted: () -> Unit,
) {
    InjectAdminStyles()

    val active = restaurants.firstOrNull { it.restaurant.id == activeId } ?: restaurants.first()
    val role = active.role
    val tabs = remember(role) { tabsFor(role) }
    // A fresh visit starts on the dashboard; F5 keeps the tab (per-browser-tab storage).
    var currentTab by remember(role) {
        val saved = com.qrkontaq.utils.SessionStorage.get("admin.activeTab")
        mutableStateOf(tabs.firstOrNull { it.name == saved } ?: tabs.first())
    }
    var mobileMenuOpen by remember { mutableStateOf(false) }

    fun selectTab(tab: AdminTab) {
        currentTab = tab
        com.qrkontaq.utils.SessionStorage.save("admin.activeTab", tab.name)
        // Each tab starts at the top — the previous tab's scroll offset must not leak over.
        window.scrollTo(0.0, 0.0)
        // Tabs are client-side state, not routes — report them as virtual pageviews.
        com.qrkontaq.core.analytics.Analytics.pageview("/admin/tab/${tab.name.lowercase()}")
    }

    com.qrkontaq.ui.components.notifications.ProvideToast {
        Box(
            Modifier.fillMaxWidth().minHeight(100.vh).attrsModifier { classes("qk-adm-bg") },
        ) {
        Sidebar(
            active = active,
            tabs = tabs,
            currentTab = currentTab,
            onSelectTab = { selectTab(it) },
            onLogout = onLogout,
        )

        Column(Modifier.attrsModifier { classes("qk-adm-main") }) {
            MobileTopBar(active) { mobileMenuOpen = true }

            Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.TopCenter) {
                // The Menu, Team and manager Dashboard tabs carry their own
                // full-width green hero (title, subtitle and language switch live
                // inside it), so they opt out of the shared 960px column and the
                // plain page header. A cook/waiter sees StaffOverview on the
                // dashboard tab, which still needs the plain header.
                val fullBleed = currentTab == AdminTab.MENU || currentTab == AdminTab.STAFF ||
                    (currentTab == AdminTab.DASHBOARD && (role == "OWNER" || role == "MANAGER"))
                // The kitchen board keeps the page header but spans the full width so the
                // three lanes have room to breathe.
                val wide = currentTab == AdminTab.ORDERS
                Column(
                    Modifier
                        .fillMaxWidth()
                        .then(if (fullBleed || wide) Modifier else Modifier.maxWidth(960.px))
                        .padding(left = 20.px, right = 20.px, top = 24.px, bottom = 48.px)
                        .attrsModifier { classes("qk-fade-in") },
                ) {
                    if (!fullBleed) {
                        // Page header + panel language on the right
                        Row(
                            Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            SpanText(
                                currentTab.title,
                                Modifier
                                    .fontSize(30.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Brand.Charcoal)
                                    .attrsModifier { classes("qk-display") },
                            )
                            // The interface-language switch lives only on the dashboard now.
                            if (currentTab == AdminTab.DASHBOARD) AdminLangDropdown(dark = false)
                        }
                        if (currentTab.subtitle.isNotBlank()) {
                            SpanText(
                                currentTab.subtitle,
                                Modifier.fontSize(14.px).color(Brand.TextSecondary).margin(top = 4.px, bottom = 20.px),
                            )
                        } else {
                            Box(Modifier.height(20.px))
                        }
                    }

                    val rid = active.restaurant.id
                    when (currentTab) {
                        AdminTab.DASHBOARD ->
                            // Managers see the restaurant; a cook/waiter sees their own work.
                            if (role == "OWNER" || role == "MANAGER") {
                                DashboardTab(repository, scope, rid, active.restaurant.currency) { selectTab(it) }
                            } else {
                                StaffOverviewTab(repository, rid, role)
                            }
                        AdminTab.MENU -> MenuTab(repository, scope, rid)
                        AdminTab.STOP_LIST -> StopListTab(repository, scope, rid)
                        AdminTab.TABLES -> TablesTab(repository, scope, rid, active.restaurant, role)
                        AdminTab.ORDERS -> OrdersTab(repository, rid, role)
                        AdminTab.STAFF ->
                            StaffTab(repository, scope, rid, active.restaurant.isPro) {
                                selectTab(AdminTab.SUBSCRIPTION)
                            }
                        AdminTab.SETTINGS ->
                            SettingsTab(repository, scope, rid, active.restaurant, role, onRestaurantDeleted) {
                                selectTab(AdminTab.SUBSCRIPTION)
                            }
                        AdminTab.SUBSCRIPTION -> SubscriptionTab(repository, scope, rid)
                    }
                }
            }
        }

        // Swipe the mobile drawer to the left to dismiss it (besides the backdrop tap).
        var drawerDragging by remember { mutableStateOf(false) }
        var drawerStartX by remember { mutableStateOf(0.0) }
        var drawerStartY by remember { mutableStateOf(0.0) }
        var drawerDx by remember { mutableStateOf(0.0) }
        DisposableEffect(drawerDragging) {
            if (!drawerDragging) {
                onDispose { }
            } else {
                val move: (org.w3c.dom.events.Event) -> Unit = { e ->
                    val (x, y) = touchPointXY(e)
                    val dx = x - drawerStartX
                    // Horizontal intent only: vertical moves keep scrolling the menu.
                    drawerDx =
                        if (kotlin.math.abs(dx) > kotlin.math.abs(y - drawerStartY)) {
                            kotlin.math.min(0.0, dx)
                        } else {
                            0.0
                        }
                }
                val up: (org.w3c.dom.events.Event) -> Unit = {
                    val shouldClose = drawerDx < -70
                    drawerDragging = false
                    drawerDx = 0.0
                    if (shouldClose) mobileMenuOpen = false
                }
                window.addEventListener("touchmove", move)
                window.addEventListener("touchend", up)
                window.addEventListener("touchcancel", up)
                onDispose {
                    window.removeEventListener("touchmove", move)
                    window.removeEventListener("touchend", up)
                    window.removeEventListener("touchcancel", up)
                }
            }
        }

        if (mobileMenuOpen) {
            Box(
                Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .backgroundColor(Color.rgba(15, 20, 17, 0.5f))
                    .zIndex(48)
                    .attrsModifier { classes("qk-fade-in") }
                    .onClick { mobileMenuOpen = false },
            )
            Column(
                Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).bottom(0.px)
                    .width(280.px)
                    .maxWidth(86.percent)
                    .zIndex(49)
                    .padding(16.px)
                    .overflow { y(Overflow.Auto) }
                    .styleModifier {
                        if (drawerDx < 0.0) property("transform", "translateX(${drawerDx}px)")
                        property("transition", if (drawerDragging) "none" else "transform .2s ease")
                    }
                    .attrsModifier {
                        classes("qk-adm-sidebg", "qk-adm-drawer-left", "qk-hide-scrollbar")
                        onTouchStart { evt ->
                            val (x, y) = touchPointXY(evt.nativeEvent)
                            drawerStartX = x
                            drawerStartY = y
                            drawerDx = 0.0
                            drawerDragging = true
                        }
                    },
            ) {
                SidebarContent(
                    active = active,
                    tabs = tabs,
                    currentTab = currentTab,
                    onSelectTab = {
                        selectTab(it)
                        mobileMenuOpen = false
                    },
                    onLogout = onLogout,
                )
            }
        }
        }
    }
}

// ---------- Desktop sidebar ----------

@Composable
private fun Sidebar(
    active: MyRestaurantDto,
    tabs: List<AdminTab>,
    currentTab: AdminTab,
    onSelectTab: (AdminTab) -> Unit,
    onLogout: () -> Unit,
) {
    Column(
        Modifier
            .padding(16.px)
            .attrsModifier { classes("qk-adm-side", "qk-hide-scrollbar") },
    ) {
        SidebarContent(
            active = active,
            tabs = tabs,
            currentTab = currentTab,
            onSelectTab = onSelectTab,
            onLogout = onLogout,
        )
    }
}

/** Shared between the fixed desktop sidebar and the mobile slide-in sheet. */
@Composable
private fun SidebarContent(
    active: MyRestaurantDto,
    tabs: List<AdminTab>,
    currentTab: AdminTab,
    onSelectTab: (AdminTab) -> Unit,
    onLogout: () -> Unit,
) {
        // Restaurant identity
        Row(
            Modifier.fillMaxWidth().margin(bottom = 22.px, top = 4.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            RestaurantAvatar(active, 38)
            Column(Modifier.margin(left = 10.px)) {
                SpanText(
                    active.restaurant.name,
                    Modifier
                        .fontSize(15.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Surface)
                        .attrsModifier { classes("qk-display", "qk-clamp1") },
                )
                SpanText(
                    roleLabel(active.role),
                    Modifier.fontSize(11.px).color(Color.rgba(255, 255, 255, 0.55f)),
                )
            }
        }

        // Link to the guest site
        Row(
            Modifier
                .fillMaxWidth()
                .margin(bottom = 18.px)
                .borderRadius(9.px)
                .padding(topBottom = 7.px)
                .cursor(Cursor.Pointer)
                .styleModifier { property("background", "rgba(15,157,110,.25)") }
                .attrsModifier { classes("qk-press"); attr("role", "button") }
                .onClick { window.open(tenantSiteUrl(active.restaurant.slug), "_blank") },
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(AdminIcons.ExternalLink, 13, "#7EE2BC")
            SpanText(
                AdminT.openSite,
                Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(Brand.MintOnDark).margin(left = 6.px),
            )
        }

        // Navigation
        tabs.forEach { tab ->
            val selected = tab == currentTab
            Row(
                Modifier
                    .fillMaxWidth()
                    .borderRadius(11.px)
                    .padding(topBottom = 10.px, leftRight = 12.px)
                    .margin(bottom = 2.px)
                    .cursor(Cursor.Pointer)
                    .attrsModifier {
                        classes("qk-adm-navitem", "qk-press")
                        if (selected) classes("qk-adm-navitem-active")
                        attr("role", "button")
                        attr("tabindex", "0")
                    }
                    .onClick { onSelectTab(tab) },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                InlineIcon(tab.iconSvg, 17, if (selected) "#7EE2BC" else "rgba(255,255,255,.55)")
                SpanText(
                    tab.title,
                    Modifier
                        .fontSize(14.px)
                        .fontWeight(if (selected) FontWeight.SemiBold else FontWeight.Medium)
                        .color(if (selected) Brand.Surface else Color.rgba(255, 255, 255, 0.65f))
                        .margin(left = 11.px),
                )
            }
        }

        Box(Modifier.fillMaxWidth().styleModifier { property("flex", "1") })

        // Logout
        Row(
            Modifier
                .fillMaxWidth()
                .borderRadius(11.px)
                .padding(topBottom = 10.px, leftRight = 12.px)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-adm-navitem", "qk-press"); attr("role", "button") }
                .onClick {
                    Session.clear()
                    onLogout()
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(AdminIcons.LogOut, 16, "rgba(255,255,255,.5)")
            SpanText(
                AdminT.logout,
                Modifier.fontSize(13.px).color(Color.rgba(255, 255, 255, 0.6f)).margin(left = 11.px),
            )
        }
}

@Composable
private fun RestaurantAvatar(entry: MyRestaurantDto, sizePx: Int) {
    val logo = entry.restaurant.logoUrl
    if (logo != null) {
        Image(
            src = logo,
            modifier = Modifier
                .size(sizePx.px)
                .minWidth(sizePx.px)
                .borderRadius(999.px)
                .objectFit(ObjectFit.Cover)
                .attrsModifier { attr("alt", entry.restaurant.name) },
        )
    } else {
        Box(
            Modifier
                .size(sizePx.px)
                .minWidth(sizePx.px)
                .borderRadius(999.px)
                .backgroundColor(Brand.Emerald),
            contentAlignment = Alignment.Center,
        ) {
            SpanText(
                entry.restaurant.name.take(1).uppercase(),
                Modifier
                    .fontSize((sizePx / 2).px)
                    .fontWeight(FontWeight.Bold)
                    .color(Brand.Surface),
            )
        }
    }
}

// ---------- Mobile chrome ----------

@Composable
private fun MobileTopBar(
    active: MyRestaurantDto,
    onMenu: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .position(Position.Sticky)
            .top(0.px)
            .zIndex(30)
            .padding(topBottom = 10.px, leftRight = 16.px)
            .attrsModifier { classes("qk-adm-mobiletop", "qk-adm-glass") },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(38.px)
                .minWidth(38.px)
                .borderRadius(999.px)
                .backgroundColor(Brand.Surface)
                .cursor(Cursor.Pointer)
                .attrsModifier { classes("qk-press"); attr("role", "button"); attr("aria-label", AdminT.tabMenu) }
                .onClick { onMenu() },
            contentAlignment = Alignment.Center,
        ) {
            InlineIcon(com.qrkontaq.ui.tenant.MenuIcons.Burger, 17, "#1F2421")
        }
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.margin(right = 8.px), horizontalAlignment = Alignment.End) {
                SpanText(
                    active.restaurant.name,
                    Modifier
                        .fontSize(15.px)
                        .fontWeight(FontWeight.Bold)
                        .color(Brand.Charcoal)
                        .styleModifier { property("max-width", "45vw") }
                        .attrsModifier { classes("qk-clamp1") },
                )
                Row(
                    Modifier
                        .margin(top = 3.px)
                        .height(22.px)
                        .borderRadius(999.px)
                        .padding(leftRight = 9.px)
                        .backgroundColor(Brand.Emerald)
                        .cursor(Cursor.Pointer)
                        .styleModifier { property("white-space", "nowrap") }
                        .attrsModifier { classes("qk-press"); attr("role", "button") }
                        .onClick { window.open(tenantSiteUrl(active.restaurant.slug), "_blank") },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    InlineIcon(AdminIcons.ExternalLink, 11, "#FFFFFF")
                    SpanText(
                        AdminT.viewSite,
                        Modifier
                            .fontSize(11.px)
                            .fontWeight(FontWeight.SemiBold)
                            .color(Brand.Surface)
                            .margin(left = 5.px),
                    )
                }
            }
            RestaurantAvatar(active, 34)
        }
    }
}

/**
 * Panel language dropdown living in the page header, opposite the title:
 * light trigger on the light admin background, the menu is a light card.
 */
@Composable
internal fun AdminLangDropdown(dark: Boolean) {
    var open by remember { mutableStateOf(false) }

    Box(Modifier.position(Position.Relative)) {
        Row(
            Modifier
                .height(32.px)
                .borderRadius(999.px)
                .padding(leftRight = 10.px)
                .cursor(Cursor.Pointer)
                .then(
                    if (dark) {
                        Modifier.styleModifier { property("background", "rgba(255,255,255,.08)") }
                    } else {
                        Modifier.backgroundColor(Brand.Surface)
                    },
                )
                .attrsModifier {
                    classes("qk-press")
                    attr("role", "button")
                    attr("aria-haspopup", "listbox")
                    attr("aria-expanded", "$open")
                    attr("aria-label", AdminT.interfaceLanguage)
                }
                .onClick { open = !open },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            InlineIcon(com.qrkontaq.ui.tenant.MenuIcons.Globe, 13, if (dark) "rgba(255,255,255,.7)" else "#6B7280")
            SpanText(
                adminLanguage.shortLabel,
                Modifier
                    .fontSize(11.px)
                    .fontWeight(FontWeight.Bold)
                    .color(if (dark) Color.rgba(255, 255, 255, 0.85f) else Brand.Charcoal)
                    .margin(left = 6.px, right = 4.px)
                    .styleModifier { property("letter-spacing", "0.04em") },
            )
            InlineIcon(
                if (open) AdminIcons.ChevronUp else AdminIcons.ChevronDown,
                12,
                if (dark) "rgba(255,255,255,.55)" else "#6B7280",
            )
        }

        if (open) {
            // Invisible backdrop: closes the menu on any outside click.
            Box(
                Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(58)
                    .onClick { open = false },
            )
            Column(
                Modifier
                    .position(Position.Absolute)
                    .top(38.px)
                    .right(0.px)
                    .minWidth(168.px)
                    .backgroundColor(Brand.Surface)
                    .borderRadius(14.px)
                    .padding(5.px)
                    .zIndex(59)
                    .boxShadow(offsetY = 8.px, blurRadius = 24.px, color = Color.rgba(31, 36, 33, 0.22f))
                    .attrsModifier { classes("qk-fade-in"); attr("role", "listbox") },
            ) {
                AdminLanguage.entries.forEach { language ->
                    val selected = language == adminLanguage
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .borderRadius(10.px)
                            .padding(topBottom = 8.px, leftRight = 11.px)
                            .cursor(Cursor.Pointer)
                            .then(if (selected) Modifier.backgroundColor(Brand.WarmBackground) else Modifier)
                            .attrsModifier {
                                classes("qk-press")
                                attr("role", "option")
                                attr("aria-selected", "$selected")
                            }
                            .onClick {
                                setAdminLanguage(language)
                                open = false
                            },
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SpanText(
                            language.fullLabel,
                            Modifier
                                .fontSize(13.px)
                                .fontWeight(if (selected) FontWeight.SemiBold else FontWeight.Medium)
                                .color(Brand.Charcoal)
                                .margin(right = 10.px),
                        )
                        if (selected) InlineIcon(AdminIcons.Check, 13, "#0F9D6E")
                    }
                }
            }
        }
    }
}

internal fun roleLabel(role: String): String =
    when (role) {
        "OWNER" -> AdminT.roleOwner
        "MANAGER" -> AdminT.roleManager
        "WAITER" -> AdminT.roleWaiter
        "COOK" -> AdminT.roleCook
        "BARTENDER" -> AdminT.roleBartender
        else -> AdminT.roleStaff
    }
